import {useLocalization} from "../../i18n";
import {useInboxApi, UserRef} from "../../api";
import React, {useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {
	ConfirmIconButton,
	DataTable,
	DataTableColumn,
	defined,
	ErrorAlert,
	useFlag,
	View,
	ViewHeader
} from "@variocube/app-ui";
import {BcaSelect} from "../../component/bca-select";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography} from "@mui/material";
import {UserSelect} from "../../component/user-select";
import {Delete} from "@mui/icons-material";

export function CreditCardManagerList() {

	const {t} = useLocalization();

	const [inboxId, setInboxId] = useState<number>();
	const [addDialogOpen, setAddDialogOpen, clearAddDialogOpen] = useFlag(false);

	const {getCreditCardManagers, addCreditCardManager, removeCreditCardManager} = useInboxApi();

	const {loading, error, result, execute} = useAsync(async (inboxId: number | undefined) => {
		if (inboxId) {
			return await getCreditCardManagers(inboxId);
		}
	}, [inboxId]);

	async function handleAdd(userId: number) {
		await addCreditCardManager(inboxId!, userId);
		await execute(inboxId);
	}

	async function handleRemove(userId: number) {
		await removeCreditCardManager(inboxId!, userId);
		await execute(inboxId);
	}

	const columns: DataTableColumn<UserRef>[] = [
		{
			field: "lastname,firstname",
			label: t("common.name"),
			sortable: false,
			component: ({row}) => <Typography>{row.displayName ?? ""}</Typography>,
		},
		{
			field: "email",
			label: t("common.email"),
			sortable: false,
		},
		{
			field: "remove",
			label: "Entfernen",
			sortable: false,
			component: ({row}) => (
				<ConfirmIconButton
					title="Entfernen"
					icon={<Delete/>}
					color="error"
					cancel={t("actions.cancel")}
					onConfirm={() => handleRemove(row.id)}
				>
					Sind Sie sicher, dass Sie {row.displayName} als Kreditkartenverwalter entfernen möchten?
				</ConfirmIconButton>
			)
		}
	];

	return (
		<View>
			<ViewHeader
				title={t("navigation.adminGroup.creditCardManagers")}
			/>
			<BcaSelect value={inboxId} onChange={setInboxId} label={t("invoices.details.bcaSelectHint")}
					   filter="CREDIT_CARD_MANAGER_EDITING"/>
			<DataTable
				columns={columns}
				rows={result ?? []}
				loading={loading}
				error={error}
				toolbar={
					<Stack direction="row" spacing={2} my={1} mx={1}>
						<Button onClick={setAddDialogOpen} disabled={!defined(inboxId)}>
							Kreditkartenverwalter hinzufügen
						</Button>
					</Stack>
				}
			/>
			<AddDialog open={addDialogOpen} onClose={clearAddDialogOpen} worksAt={inboxId} onSave={handleAdd}/>
		</View>
	);
}

interface AddDialogProps {
	open: boolean;
	onClose: () => void;
	worksAt?: number;
	onSave: (userId: number) => Promise<any>;
}

function AddDialog(props: Readonly<AddDialogProps>) {
	const {open, onClose, worksAt, onSave} = props;
	const {t} = useLocalization();
	const [userId, setUserId] = useState<number>();

	const {loading, error, execute} = useAsyncCallback(async () => {
		if (userId) {
			await onSave(userId);
			onClose();
		}
	});

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="dialog-title"
				aria-describedby="dialog-description">
			<DialogTitle>Kreditkartenverwalter hinzufügen</DialogTitle>
			<DialogContent>
				<UserSelect value={userId} onChange={setUserId} worksAt={worksAt}/>
			</DialogContent>
			{error && <DialogContent><ErrorAlert error={error}/></DialogContent>}
			<DialogActions>
				<Button onClick={onClose} color="inherit">{t("actions.cancel")}</Button>
				<Button onClick={execute} color="primary" disabled={loading}>Kreditkartenverwalter hinzufügen</Button>
			</DialogActions>
		</Dialog>
	)
}
